import axios from 'axios';
import { captureCheckoutKeys } from '@/lib/checkoutKey';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * API base URL.
 * - Public customer pages (checkout, landing, success) served from a store's
 *   custom domain → use SAME origin so calls stay on `pay.boutique-x.com`. This
 *   keeps the main platform URL invisible to the customer AND isolates traffic
 *   per domain at the CDN level.
 * - Admin pages (login, dashboard) and dev preview → use the configured
 *   REACT_APP_BACKEND_URL.
 *
 * Detection: when the current host matches the configured BACKEND_URL host or
 * looks like a dev preview, fall back to BACKEND_URL. Otherwise (custom domain),
 * use window.location.origin.
 */
function resolveApiBase() {
  // Build-time fallback for SSR/Node (no window).
  if (typeof window === 'undefined') return `${BACKEND_URL}/api`;
  try {
    const currentHost = window.location.host;
    const backendHost = new URL(BACKEND_URL).host;
    // Same host → use configured base (admin + dev preview).
    if (currentHost === backendHost) return `${BACKEND_URL}/api`;
    // Internal Emergent infra hosts (preview, deploy clusters) → keep backend URL.
    if (/\.emergentagent\.com$|\.emergent\.host$|\.deploy\.emergentagent\.com$/.test(currentHost)) {
      return `${BACKEND_URL}/api`;
    }
    // Real custom domain → call APIs on the same origin (relative).
    return `${window.location.origin}/api`;
  } catch {
    return `${BACKEND_URL}/api`;
  }
}

export const API = resolveApiBase();

const TOKEN_KEY = 'admin_token';
const USER_KEY = 'admin_user';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const getSessionId = () => { try { const t = localStorage.getItem(TOKEN_KEY); return t ? JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).sid || '' : ''; } catch { return ''; } };
export const clearToken = () => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); };

// Selected payment processor section ('stripe' | 'viva'). The whole admin is
// split by PSP: every admin GET is scoped to the active processor (see interceptor).
const PSP_KEY = 'ec_psp';
export const getPsp = () => localStorage.getItem(PSP_KEY) || 'stripe';
export const setPsp = (p) => localStorage.setItem(PSP_KEY, p);
export const getCurrentUser = () => {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); }
  catch { return null; }
};
export const setCurrentUser = (u) => localStorage.setItem(USER_KEY, JSON.stringify(u));
export const hasRole = (...roles) => {
  const u = getCurrentUser();
  return u && roles.includes(u.role);
};

export const api = axios.create({ baseURL: API, timeout: 20000 });

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  // Scope every admin GET to the active processor section (Stripe / Viva).
  if ((config.method || 'get').toLowerCase() === 'get') {
    config.params = { psp: getPsp(), ...(config.params || {}) };
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (axios.isCancel(err) || err.config?.signal?.aborted) return Promise.reject(err);
    if (err?.response?.status === 401) {
      // Step-up (area PIN) failures are not a dead session: let the caller re-ask the PIN.
      const detail = String(err.response?.data?.detail || '');
      if (/PIN/i.test(detail) || String(err.config?.url || '').includes('/auth/area-verify')) {
        if (/expir/i.test(detail)) window.dispatchEvent(new CustomEvent('stepup-expired'));
        return Promise.reject(err);
      }
      const wasInvite = window.location.pathname.startsWith('/invite');
      const hadSession = !!localStorage.getItem(TOKEN_KEY);
      clearToken();
      if (hadSession && !window.location.pathname.endsWith('/login') && !window.location.pathname.includes('/activation/')) {
        window.location.assign(wasInvite ? '/invite/login' : '/admin/login');
      }
      return Promise.reject(err);
    }
    // Auto-retry transient failures on idempotent GETs only (never POST/PUT, so
    // we never double-submit a payment or a create). Covers cold starts, brief
    // 502/503/504 from the edge, timeouts and network blips — the #1 cause of
    // "I had to refresh a few times for it to work".
    const config = err.config || {};
    const method = (config.method || 'get').toLowerCase();
    const status = err?.response?.status;
    const transient = !err.response || [502, 503, 504].includes(status) || err.code === 'ECONNABORTED';
    config.__retryCount = config.__retryCount || 0;
    if (method === 'get' && transient && config.__retryCount < 2) {
      config.__retryCount += 1;
      await new Promise((res) => setTimeout(res, 500 * config.__retryCount));
      return api(config);
    }
    return Promise.reject(err);
  }
);


// Public (customer-facing) axios instance: timeout + transient GET retry, but
// NO auth header and NO 401 handling. Used by checkout/landing pages.
export const publicApi = axios.create({ timeout: 20000 });
captureCheckoutKeys(publicApi);
captureCheckoutKeys(axios);
publicApi.interceptors.response.use(
  (r) => r,
  async (err) => {
    const config = err.config || {};
    const method = (config.method || 'get').toLowerCase();
    const status = err?.response?.status;
    const transient = !err.response || [502, 503, 504].includes(status) || err.code === 'ECONNABORTED';
    config.__retryCount = config.__retryCount || 0;
    if (method === 'get' && transient && config.__retryCount < 2) {
      config.__retryCount += 1;
      await new Promise((res) => setTimeout(res, 500 * config.__retryCount));
      return publicApi(config);
    }
    return Promise.reject(err);
  }
);
